import {
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import React from "react";
import { email, Form, required, SelectInput, TextInput } from "react-admin";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import sendRequest from "../dataProvider/sendRequest";
import HMLogo from "../layout/HMLogo";
import DownloadFieldByPdfLink from "../unit/DownloadFieldByPdfLink";
import useNotify from "../unit/useNotify";
const PageHeader = () => (
  <Box
    sx={{
      padding: 2,
      textAlign: "left",
      borderBottom: "7px solid #04983F",
      display: "flex",
      alignItems: "center",
      color: "grey",
    }}
  >
    Powered by <HMLogo />
  </Box>
);
const PageFooter = () => (
  <Box
    sx={{
      padding: 2,
      textAlign: "center",
      bottom: 0,
      left: 0,
      right: 0,
    }}
  >
    <Typography variant="body2" sx={{ color: "grey" }}>
      Copyright Happy Mango {new Date().getFullYear()}
    </Typography>
  </Box>
);
type FormValues = {
  email: string;
};

const EmployerVerifyFormFields = ({ loading, bankerInfo }: any) => {
  const { watch, setValue } = useFormContext<any>();
  const employedAtCompany = watch("employed_at_company") || "";
  const currentlyOnLeave = watch("currently_on_leave") || "";
  const recommendApplication = watch("recommend_application") || "";
  const shouldShowRecommendApplication =
    employedAtCompany === "Yes" && !!currentlyOnLeave;
  const shouldShowVerificationFields =
    employedAtCompany === "Yes" &&
    // currentlyOnLeave === "Active" &&
    recommendApplication === "Yes";

  return (
    <>
      <TextField
        label="Company Name"
        variant="outlined"
        fullWidth
        value={bankerInfo?.company || ""}
        InputProps={{
          readOnly: true,
          disabled: true,
        }}
      />

      <TextField
        label="Applicant Name"
        variant="outlined"
        sx={{ mt: 2 }}
        fullWidth
        value={bankerInfo?.firstname + " " + bankerInfo?.lastname || ""}
        InputProps={{
          readOnly: true,
          disabled: true,
        }}
      />

      <SelectInput
        source="employed_at_company"
        variant="outlined"
        sx={{ mt: 2 }}
        label="Is this applicant currently employed at your company ?"
        choices={[
          {
            id: "No",
            name: "Applicant has never been employed at the Company",
          },
          {
            id: "NoLogerEmployed",
            name: "Applicant is no longer employed at the Company",
          },
          {
            id: "EmployedLess12Months",
            name: "Applicant has been employed at the Company for less than 12 months",
          },
          {
            id: "Yes",
            name: "Applicant has been employed at the Company for more than 12 months",
          },
        ]}
        validate={required("This field is required")}
        onChange={(event) => {
          const value = event.target.value;
          if (value !== "Yes") {
            setValue("currently_on_leave", "");
            setValue("recommend_application", "");
          }
        }}
        fullWidth
        disabled={loading}
      />
      {employedAtCompany === "Yes" && (
        <>
          <SelectInput
            source="currently_on_leave"
            variant="outlined"
            label="Describe the applicant’s employment status"
            choices={[
              {
                id: "Yes",
                name: "Currently or expected soon to be on leave",
              },
              {
                id: "No",
                name: "Employment NOT in good standing",
              },
              {
                id: "Active",
                name: "Active employment in good standing",
              },
            ]}
            sx={{ mt: 0 }}
            fullWidth
            disabled={loading}
            onChange={(event) => {
              const value = event.target.value;
              setValue(
                "recommend_application",
                value === "Yes" || value === "No" ? "No" : ""
              );
            }}
          />
          {shouldShowRecommendApplication && (
            <SelectInput
              source="recommend_application"
              variant="outlined"
              label="Do you recommend we move forward with this application?"
              choices={[
                { id: "Yes", name: "Yes" },
                { id: "No", name: "No" },
              ]}
              required={false}
              sx={{ mt: 0 }}
              fullWidth
              disabled={loading}
            />
          )}
        </>
      )}
      {shouldShowVerificationFields && (
        <>
          <SelectInput
            source="completed_internal_survey"
            variant="outlined"
            label="Has this applicant completed the internal survey ?"
            choices={[
              { id: "2", name: "We do not have a survey" },
              { id: "1", name: "Yes" },
              { id: "0", name: "No" },
            ]}
            validate={required("This field is required")}
            fullWidth
            disabled={loading}
          />
          <TextInput
            source="sJobTitle"
            label="Occupation / Job Title"
            variant="outlined"
            validate={required("This field is required")}
            fullWidth
            disabled={loading}
          />
          <TextInput
            source="date"
            label="Employment Start Date"
            variant="outlined"
            type="date"
            validate={required("This field is required")}
            fullWidth
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />

          <TextInput
            source="income"
            label="Current Annual Pre-Tax Gross Income"
            variant="outlined"
            type="number"
            validate={required("This field is required")}
            fullWidth
            disabled={loading}
            InputProps={{
              startAdornment: <span style={{ marginRight: "8px" }}>$</span>,
            }}
          />
          <Typography variant="body1" gutterBottom>
            Employer Representative:
          </Typography>
          <TextInput
            source="employername"
            label="Employer Representative Name"
            variant="outlined"
            validate={required("This field is required")}
            fullWidth
            disabled={loading}
          />
          <TextInput
            source="employeremail"
            label="Employer Representative Email"
            variant="outlined"
            type="email"
            validate={[required("This field is required"), email()]}
            fullWidth
            disabled={loading}
          />
        </>
      )}
    </>
  );
};

const EmployerVerifyPage = () => {
  const params = useParams();
  const token = params.token;
  const [loading, setLoading] = React.useState(false);
  const [bankerInfo, setBankerInfo] = React.useState({});
  const [pdflink, setPdfLink] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const handleSubmit = async (auth: FormValues) => {
    setLoading(true);
    const reqUrl = "verify-employer";
    const formJson = {
      token: token,
      ...auth,
    };
    const response = await sendRequest(reqUrl, {
      id: 0,
      data: formJson,
    });
    console.log("==========", response?.code, response?.data?.file_hash);
    if (response && (response.status == "200" || response.code == "200")) {
      setErrorMessage("");
      setPdfLink(response?.data?.file_hash);
      setLoading(false);
    } else {
      if (response && response.message) {
        setErrorMessage(response?.message);
      } else {
        setErrorMessage("Data save failed");
      }

      setLoading(false);
    }
  };

  React.useEffect(() => {
    const fetchBankerInfo = async () => {
      if (token) {
        const reqUrl = "employer/actions/getinfo";
        const response = await sendRequest(reqUrl, {
          id: 0,
          data: { token },
        });
        if (response && response.code == "200") {
          setBankerInfo(response.data);
          setErrorMessage("");
        } else {
          errorNotify("Failed to fetch banker info");
          setErrorMessage(response?.message || "Failed to fetch banker info");
        }
      }
    };
    fetchBankerInfo();
  }, [token]);

  if (!bankerInfo) {
    return (
      <Typography
        sx={{ textAlign: "center", m: 5, fontSize: "1.2rem", color: "red" }}
      >
        Loading information...
      </Typography>
    );
  }
  return (
    <>
      <PageHeader />
      {errorMessage == "" ? (
        <>
          {pdflink && (
            <>
              <Box
                sx={{
                  padding: 2,
                  textAlign: "center",
                  minHeight: "500px",
                }}
              >
                <Typography variant="h4" gutterBottom>
                  Information saved!
                </Typography>

                <Typography variant="body1" gutterBottom>
                  Click{" "}
                  <DownloadFieldByPdfLink
                    linktoken={pdflink}
                    filename={"Employer_Verification_Form.pdf"}
                    label="Download Completed Form"
                    sx={{ fontSize: "1.2rem", verticalAlign: "middle" }}
                  />{" "}
                  to download the completed form.
                </Typography>
              </Box>
            </>
          )}
          {!pdflink && (
            <Box
              sx={{ padding: 2, width: "100%", height: "100%", margin: "auto" }}
            >
              <Box sx={{ maxWidth: "800px", margin: "auto" }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={3} md={3}>
                    {bankerInfo?.banklogo && (
                      <img
                        src={
                          bankerInfo?.banklogo ||
                          "/images/happymango-logo-b.png"
                        }
                        style={{ maxWidth: "90%" }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={9} md={9}>
                    <Typography variant="body1" gutterBottom>
                      Employment Verification Form
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ mt: 2, color: "gray" }}
                    >
                      To be eligible for the Employee Opportunity Loan your
                      employee must be in good standing and has been actively
                      employed for a minimum of 12 months with the most recent 6
                      months being full-time.
                    </Typography>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={{ mt: 2, color: "gray" }}
                    >
                      You may update and resubmit this information within 30
                      days after receiving the initial request email until an
                      underwriting decision has been made on the loan
                      application.
                    </Typography>
                    <Form onSubmit={handleSubmit} noValidate>
                      <EmployerVerifyFormFields
                        loading={loading}
                        bankerInfo={bankerInfo}
                      />
                      <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        disabled={loading}
                        sx={{
                          backgroundColor: "#00ccb1",
                          "&:hover": {
                            backgroundColor: "#00b39b",
                          },
                        }}
                      >
                        {loading && (
                          <CircularProgress size={25} thickness={2} />
                        )}
                        Save
                      </Button>
                    </Form>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Typography
          sx={{ textAlign: "center", m: 5, fontSize: "1.2rem", color: "red" }}
        >
          {errorMessage}
        </Typography>
      )}
      <PageFooter />
    </>
  );
};
export default EmployerVerifyPage;
